import React, { useEffect } from 'react';
import { get, isEmpty } from 'lodash';
import { isAppModule } from '@src/utils/constants';
import { useQuery } from 'react-query';
import instance from '@api/axiosInstance';
import getMetricsData from '@src/api/metricContainer';
import useTimeseriesQuery from './useTimeseriesQuery';
import { DATA_TAGS, useGetData, useSetData } from './useData';

const defaultMetricValue = { total: null };
const defaultResponseValue = { totals: {} };

// hook to fetch dod data
export function useLiveSummary({ enabled = true, dataTag = DATA_TAGS.liveSummary }) {
  const {
    query: baseQuery,
    metrics = [],
    realTimeOnTrends,
    dataScope,
    usecaseKey,
  }: any = useTimeseriesQuery();

  const query = {
    ...baseQuery,
    ignoreInterval: true,
    granularity: 'ALL',
    options: { withTotals: true },
  };

  if (isAppModule) {
    query.options.dataScope = dataScope;
    query.options.presetKey = usecaseKey;
  }

  const setLiveSummary = useSetData({ tag: dataTag, defaultValue: defaultResponseValue });

  const { data, isFetching } = useQuery(
    ['live-summary', query],
    () => {
      return getMetricsData({ queries: [query] }, {}, instance).then((response) => {
        const { data, totals } = get(response, ['data', '0'], []);
        return { data, totals };
      });
    },
    {
      refetchOnWindowFocus: false,
      retry: true,
      enabled: enabled && realTimeOnTrends && metrics.length > 0,
      staleTime: 20 * 1000, // to deduplicate requests
      placeholderData: {},
      keepPreviousData: realTimeOnTrends,
      select: React.useCallback(
        (response) => {
          if (!realTimeOnTrends || isEmpty(response)) {
            return defaultResponseValue;
          }
          return response;
        },
        [realTimeOnTrends],
      ),
    },
  );

  useEffect(() => {
    setLiveSummary(data);
  }, [data]);

  return { isFetching, data };
}

export function useLiveSummaryData({ metric = null, dataTag = DATA_TAGS.liveSummary }) {
  const { totals }: any = useGetData({ tag: dataTag, defaultValue: defaultResponseValue });
  if (metric) {
    return metric in totals ? { total: totals[metric] } : defaultMetricValue;
  }
  return { totals };
}

export default useLiveSummary;
